import React from "react";
import { Row, Col, Divider, Empty, Button } from "antd";
import {
  BankOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  PrinterOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import THEME from "../theme";
import { formatMoney, formatDate } from "../utils";
import StyledCard from "../components/StyledCard";
import StatBox from "../components/StatBox";
import InfoItem from "../components/InfoItem";

const LoanNewInfoTab = ({ judgeData, handlePrintCardDebtor, beeypupData }) => {
  const loan = judgeData?.loanNew;
  if (!loan) return <Empty description="ไม่มีข้อมูล" />;

  // A  ไม่ค้างงวดเลย                                                1.00
  // B  ค้าง1งวด                                                     1.00
  // C  ค้าง 2 งวด                                                   1.00
  // D  ค้าง 3 งวด                                                   1.25
  // X  ลูกค้ามีปัญหา                                                2.00
  // F  ค้าง 5 งวด                                                   1.50
  // V  VIP
  // Z  ลูกค้าที่ห้ามปล่อยสินเชื่อเด็ดขาด                            10.0

  const calculatLessPay = () => {
    const grade = judgeData?.loanNew?.GRDCOD;
    const totUpay = judgeData?.loanNew?.TOT_UPAY;
    let result;

    if (grade === "A" || grade === "B" || grade === "B") {
      result = totUpay * 1;
    } else if (grade === "D") {
      result = totUpay * 1.25;
    } else if (grade === "X") {
      result = totUpay * 2;
    } else if (grade === "F") {
      result = totUpay * 1.5;
    } else if (grade === "Z") {
      result = totUpay * 10;
    } else {
      result = totUpay;
    }

    return `${formatMoney(result) || "-"} บาท`;
  };

  const dataBeeypup = () => {
    if (!Array.isArray(beeypupData) || beeypupData.length === 0) return "-";

    const result = beeypupData.reduce((sum, item) => {
      const beeypup = Number(item?.BEEYPUP) || 0;
      const payBeeypup = Number(item?.PAY_BEEYPUP) || 0;
      return sum + (beeypup - payBeeypup);
    }, 0);

    return `${formatMoney(result) || "-"} บาท`;
  };

  return (
    <StyledCard
      title="ข้อมูลสัญญา(ใหม่)"
      icon={<BankOutlined style={{ color: THEME.primary, fontSize: 18 }} />}
      style={{ marginBottom: 16 }}
      extra={
        <Button
          type="primary"
          icon={<PrinterOutlined />}
          onClick={handlePrintCardDebtor}
          size="small"
        >
          พิมพ์
        </Button>
      }
    >
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={8} md={6}>
          <StatBox
            title="ยอดจัดทั้งหมด"
            value={loan?.NCSHPRC}
            color={THEME.primary}
            icon={<DollarOutlined />}
          />
        </Col>
        <Col xs={12} sm={8} md={6}>
          <StatBox
            title="ชำระแล้ว"
            value={loan?.LOAN_NEW_SMPAY}
            color={THEME.success}
            icon={<CheckCircleOutlined />}
          />
        </Col>
        <Col xs={12} sm={8} md={6}>
          <StatBox
            title="ค่างวด"
            value={loan?.TOT_UPAY}
            color={THEME.info}
            icon={<WarningOutlined />}
          />
        </Col>
        <Col xs={12} sm={8} md={6}>
          <StatBox
            title="คงเหลือ"
            value={Math.max(0, loan?.NCSHPRC - loan?.LOAN_NEW_SMPAY)}
            color="green"
            icon={<DollarOutlined />}
          />
        </Col>
      </Row>
      <Divider style={{ margin: "20px 0" }} />
      <Row gutter={[32, 16]}>
        <Col xs={12} sm={8} md={4}>
          <InfoItem label="สัญญาเก่าคือ" value={`${loan?.ISSUNO || "-"}`} />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <InfoItem
            label="สัญญาใหม่"
            value={`${judgeData?.loanNew?.NEWCONTNO || "-"}`}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <InfoItem
            label="วันทำสัญญา"
            value={formatDate(loan?.SDATE)}
            icon={<CalendarOutlined />}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <InfoItem label="งวดแรก" value={formatDate(loan?.FDATE)} />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <InfoItem label="งวดสุดท้าย" value={formatDate(loan?.LDATE)} />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <InfoItem label="จำนวนงวด" value={`${loan?.T_NOPAY || 0} งวด`} />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <InfoItem
            label="งวดละ"
            value={`${formatMoney(loan?.TOT_UPAY)} บาท`}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <InfoItem label="ชำระล่าสุด" value={formatDate(loan?.LPAYD)} />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <InfoItem
            label="ยอดชำระล่าสุด"
            value={`${formatMoney(loan?.LPAYA)} บาท`}
          />
        </Col>

        <Col xs={12} sm={8} md={4}>
          <InfoItem
            label="ค้างจาก"
            value={`${judgeData?.loanNew?.EXP_FRM || "-"}`}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <InfoItem
            label="ค้างถึง"
            value={`${judgeData?.loanNew?.EXP_TO || "-"}`}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <InfoItem
            label="ค้างประมาณ"
            value={`${judgeData?.loanNew?.EXP_PRD || "-"}`}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <InfoItem
            label="ค้างจริง"
            value={`${judgeData?.loanNew?.HLDNO || "-"}`}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <InfoItem
            label="ยอดค้าง"
            value={`${formatMoney(judgeData?.loanNew?.EXP_AMT) || "-"}`}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <InfoItem
            label="เกรด"
            value={`${judgeData?.loanNew?.GRDCOD || "-"}`}
          />
        </Col>

        <Col xs={12} sm={8} md={4}>
          <InfoItem label="จ่ายขั้นต่ำ" value={calculatLessPay()} />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <InfoItem label="เบี้ยปรับ" value={dataBeeypup()} />
        </Col>
      </Row>
    </StyledCard>
  );
};

export default LoanNewInfoTab;
